import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import { Navigate, NavLink, Outlet, useLocation, useNavigationType } from 'react-router-dom'
import { FullLoader } from '../../components/Loader'
import { STORAGE_PREFIX } from '../../config'
import { useAuth, useHasAccess } from '../../hooks/auth'
import { OS_PERM } from '../../types/os'
import { ESTOQUE_PERM } from '../../types/estoque'
import { PREVISOES_PERM } from '../../types/previsoes'
import { AbasEstoque } from './AbasEstoque'
import { ImportBatchProvider, useImportBatch } from './ImportBatchContext'
import { AcessosMenu, GrupoMenu, ItemMenu, grupoDaRota, itemAtivo, montarMenu, mostrarAbas } from './menu'
import { IconeDoMenu, SetaDoGrupo } from './menuIcones'
import { useSessionExpired } from './useSessionExpired'
import {
  BD,
  Content,
  Hamburger,
  InternalShell,
  MainCol,
  Sidebar,
  SidebarBrand,
  SidebarChevron,
  SidebarGroup,
  SidebarGroupBox,
  SidebarLink,
  SidebarOverlay,
  Topbar,
} from './styles'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'

const CHAVE_FECHADOS = STORAGE_PREFIX + 'menuGruposFechados'

/**
 * OP-3605 — os grupos que a pessoa fechou, entre sessões.
 *
 * Não é enfeite: com os cinco grupos abertos o menu fica MAIS alto que a
 * lista plana de antes. O ganho de altura vem de lembrar o que ela fechou.
 */
function lerFechados(): string[] {
  try {
    const cru = window.localStorage.getItem(CHAVE_FECHADOS)
    const lista = cru ? JSON.parse(cru) : []
    return Array.isArray(lista) ? lista.filter((x: unknown) => typeof x === 'string') : []
  } catch {
    return []
  }
}

function gravarFechados(ids: string[]): void {
  try {
    window.localStorage.setItem(CHAVE_FECHADOS, JSON.stringify(ids))
  } catch {
    /* guia anônima ou storage cheio — o menu só deixa de lembrar */
  }
}

/** Link de acompanhamento com badge do tamanho do lote atual (lê o provider). */
const AcompanhamentoLink: FC<{ item: ItemMenu; ativo: boolean; onClick: () => void }> = ({
  item,
  ativo,
  onClick,
}) => {
  const { batch } = useImportBatch()
  return (
    <NavLink to={item.to} style={{ textDecoration: 'none' }} onClick={onClick}>
      <SidebarLink $active={ativo}>
        <IconeDoMenu nome={item.icone} />
        <span>{item.rotulo}</span>
        {batch.length > 0 && (
          <span
            style={{
              marginLeft: 'auto',
              background: ativo ? '#ffffff' : BD.redLight,
              color: BD.red,
              borderRadius: 9999,
              fontSize: 11,
              fontWeight: 700,
              padding: '1px 7px',
            }}
          >
            {batch.length}
          </span>
        )}
      </SidebarLink>
    </NavLink>
  )
}

export const InternalLayout: FC = () => {
  const { status, verifyJwt } = useAuth()
  const location = useLocation()
  const canTecnicos = useHasAccess(OS_PERM.GERENCIAR_TECNICOS)
  const canConfig = useHasAccess(OS_PERM.CONFIGURAR)
  const canImport = useHasAccess(OS_PERM.IMPORTAR)
  const canAnalytics = useHasAccess(OS_PERM.ANALYTICS)
  const canEstoque = useHasAccess(ESTOQUE_PERM.VISUALIZAR) // OP-3605
  const canSolicitar = useHasAccess(ESTOQUE_PERM.SOLICITAR) // OP-3605
  const canPatrimonio = useHasAccess(ESTOQUE_PERM.PATRIMONIO) // OP-3605
  const canNotificacoes = useHasAccess(ESTOQUE_PERM.NOTIFICACOES)
  const canPrevisoes = useHasAccess(PREVISOES_PERM.VISUALIZAR) // 5897
  const [menuOpen, setMenuOpen] = useState(false)
  const sessionExpired = useSessionExpired()
  const [, , removeCookie] = useCookies()

  // OP-3462: limpeza do cookie fantasma. Versões antigas gravavam o userJWT sem
  // path — um refresh de token disparado em /interno criava um SEGUNDO cookie
  // (Path=/interno) que sombreava o login novo SÓ nas rotas do BD Interno
  // ("deslogar e logar não resolve; só guia anônima"). Remoção incondicional,
  // inofensiva quando o cookie não existe.
  useEffect(() => {
    try {
      removeCookie(STORAGE_PREFIX + 'userJWT', { path: '/interno' })
    } catch {
      /* cookie API indisponível — ignora */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const acessos: AcessosMenu = useMemo(
    () => ({
      importacoes: canImport,
      analytics: canAnalytics,
      tecnicos: canTecnicos,
      config: canConfig,
      estoque: canEstoque,
      solicitar: canSolicitar,
      patrimonio: canPatrimonio,
      notificacoes: canNotificacoes,
      previsoes: canPrevisoes,
    }),
    [canImport, canAnalytics, canTecnicos, canConfig, canEstoque, canSolicitar, canPatrimonio, canNotificacoes, canPrevisoes],
  )
  const grupos: GrupoMenu[] = useMemo(() => montarMenu(acessos), [acessos])
  const grupoAtivo = grupoDaRota(grupos, location.pathname)
  const [fechados, setFechados] = useState<string[]>(lerFechados)

  // O grupo da tela aberta nunca fica fechado. Sem isto, quem chega por URL
  // direta ou por um botão de outra tela não enxerga onde está.
  useEffect(() => {
    if (!grupoAtivo) return
    setFechados((f) => (f.indexOf(grupoAtivo) < 0 ? f : f.filter((id) => id !== grupoAtivo)))
  }, [grupoAtivo])

  const navegacao = useNavigationType()
  const ultimaNavegacao = useRef(navegacao)
  ultimaNavegacao.current = navegacao
  useEffect(() => {
    if (ultimaNavegacao.current !== 'POP') window.scrollTo(0, 0)
  }, [location.pathname])

  const alternarGrupo = (id: string) => {
    setFechados((f) => {
      const proximo = f.indexOf(id) < 0 ? f.concat(id) : f.filter((x) => x !== id)
      gravarFechados(proximo)
      return proximo
    })
  }

  if (!LOCAL_EXECUTION && status === 'not-logged' && location.pathname !== '/auth') {
    return <Navigate to="/auth" replace />
  }
  if (!LOCAL_EXECUTION && status !== 'logged' && location.pathname !== '/auth') {
    return <FullLoader />
  }
  // Sessão expirada (token vencido) enquanto logado: em vez de ficar "preso", manda
  // pro /auth, que valida e redireciona corretamente (fluxo de auth do app, no React).
  // `sessionExpired` cobre o caso da aba parada: o timer/foco do useSessionExpired
  // força este re-render no instante em que o token vence.
  if (
    !LOCAL_EXECUTION &&
    status === 'logged' &&
    (sessionExpired || !verifyJwt()) &&
    location.pathname !== '/auth'
  ) {
    return <Navigate to="/auth" replace />
  }

  const close = () => setMenuOpen(false)
  const comAbas = mostrarAbas(location.pathname)

  return (
    <ImportBatchProvider>
      <InternalShell>
        <Sidebar $open={menuOpen}>
          <SidebarBrand>
            BD <span>Interno</span>
          </SidebarBrand>
          {grupos.map((g) => {
            const aberto = fechados.indexOf(g.id) < 0
            return (
              <SidebarGroupBox key={g.id}>
                <SidebarGroup type="button" aria-expanded={aberto} onClick={() => alternarGrupo(g.id)}>
                  <span style={{ flexGrow: 1, textAlign: 'left' }}>{g.titulo}</span>
                  <SidebarChevron $open={aberto}>
                    <SetaDoGrupo />
                  </SidebarChevron>
                </SidebarGroup>
                {aberto &&
                  g.itens.map((item) => {
                    const ativo = itemAtivo(item, location.pathname)
                    if (item.contador === 'importacoes') {
                      return (
                        <AcompanhamentoLink key={item.to} item={item} ativo={ativo} onClick={close} />
                      )
                    }
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        style={{ textDecoration: 'none' }}
                        onClick={close}
                      >
                        <SidebarLink $active={ativo}>
                          <IconeDoMenu nome={item.icone} />
                          <span>{item.rotulo}</span>
                        </SidebarLink>
                      </NavLink>
                    )
                  })}
              </SidebarGroupBox>
            )
          })}
        </Sidebar>
        <SidebarOverlay $open={menuOpen} onClick={close} />

        <MainCol>
          <Topbar>
            <Hamburger onClick={() => setMenuOpen((o) => !o)} aria-label="Menu">
              ☰
            </Hamburger>
            <strong>
              BD <span style={{ color: '#E40521' }}>Interno</span>
            </strong>
          </Topbar>
          <Content $abas={comAbas}>
            <Outlet />
          </Content>
        </MainCol>
        <AbasEstoque acessos={acessos} caminho={location.pathname} />
      </InternalShell>
    </ImportBatchProvider>
  )
}

export default InternalLayout
